import { useEffect, useMemo, useState } from 'react';
import { Modal, Input, List, Tag, Spin, Empty, Avatar, message } from 'antd';
import { SearchOutlined, UserOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useDebounce } from '../../hooks/useDebounce';
import {
  searchImpersonationUsers,
  type ImpersonationSearchResult,
} from '../../api/impersonation';

interface AssumeUserModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Debug / Assume-User search modal (NON-PRODUCTION ONLY).
 * Search by name/email, see each user's type/role, select to begin impersonation.
 */
export default function AssumeUserModal({ open, onClose }: AssumeUserModalProps) {
  const { startImpersonation, currentUser } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ImpersonationSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState<number | null>(null);
  const debouncedQuery = useDebounce(query, 300);

  // Reset state each time the modal is opened.
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setStarting(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    searchImpersonationUsers(debouncedQuery.trim(), 25)
      .then((rows) => {
        if (!cancelled) setResults(rows);
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, open]);

  const visibleResults = useMemo(
    () => results.filter((u) => u.id !== currentUser?.id),
    [results, currentUser?.id],
  );

  const handleSelect = async (user: ImpersonationSearchResult) => {
    setStarting(user.id);
    try {
      await startImpersonation(user.id);
      window.location.reload();
    } catch (err: any) {
      message.error(
        err?.response?.data?.error || err?.response?.data?.message || 'Could not start impersonation',
      );
      setStarting(null);
    }
  };

  return (
    <Modal
      title="Debug Mode — Assume User"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={560}
    >
      <p style={{ marginTop: 0, color: 'var(--rb-color-text-dark-secondary)', fontSize: 13 }}>
        Search for a user to act as. The app will behave as if you are that user until you
        stop. Your admin identity is preserved and every action is audited.
      </p>
      <Input
        autoFocus
        size="large"
        allowClear
        prefix={<SearchOutlined />}
        placeholder="Search by name or email…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Search users to assume"
        style={{ marginBottom: 12 }}
      />

      <div style={{ maxHeight: 360, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : visibleResults.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No matching users" />
        ) : (
          <List
            dataSource={visibleResults}
            renderItem={(user) => (
              <List.Item
                role="button"
                tabIndex={0}
                aria-label={`Assume ${user.full_name}`}
                onClick={() => starting === null && handleSelect(user)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && starting === null) {
                    e.preventDefault();
                    handleSelect(user);
                  }
                }}
                style={{
                  cursor: starting === null ? 'pointer' : 'default',
                  padding: '10px 8px',
                  borderRadius: 8,
                  opacity: starting !== null && starting !== user.id ? 0.5 : 1,
                }}
              >
                <List.Item.Meta
                  avatar={<Avatar icon={<UserOutlined />} />}
                  title={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {user.display_name || user.full_name}
                      {user.is_admin && <Tag color="gold">Admin</Tag>}
                      {user.user_type_name && <Tag>{user.user_type_name}</Tag>}
                    </span>
                  }
                  description={user.email}
                />
                {starting === user.id && <Spin size="small" />}
              </List.Item>
            )}
          />
        )}
      </div>
    </Modal>
  );
}
